import Keycloak from 'keycloak-js'
import type { IAuthClient } from '../core/ports/IAuthClient'

/**
 * KeycloakAuthClient — concrete implementation of IAuthClient backed by keycloak-js.
 *
 * This is the ONLY file in the codebase that imports keycloak-js.
 * Replacing Keycloak with Auth0 means writing a new class here — nothing else changes.
 */
export class KeycloakAuthClient implements IAuthClient {
  private readonly kc: Keycloak
  private readonly realmUrl: string
  private sessionEndedListeners: (() => void)[] = []

  constructor(config: { url: string; realm: string; clientId: string }) {
    this.kc = new Keycloak(config)
    this.realmUrl = `${config.url}/realms/${config.realm}`
    // keycloak-js clears its tokens when a refresh is refused (the Keycloak session is over).
    this.kc.onAuthLogout = () => this.sessionEnded()
  }

  onSessionEnded(listener: () => void): void {
    this.sessionEndedListeners.push(listener)
  }

  private sessionEnded() {
    for (const listener of this.sessionEndedListeners) listener()
  }

  init(): Promise<boolean> {
    return this.kc.init({
      onLoad: 'check-sso',
      // Without this, check-sso does its login-status check via a full top-window
      // redirect to the Keycloak server and back — if that server is unreachable,
      // the whole app is replaced by the dead auth server instead of just failing
      // to log in. Routing the check through a hidden iframe (this static page,
      // which was already shipped in public/ but never wired up) keeps the app
      // on-screen even when Keycloak is down.
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      pkceMethod: 'S256',
      checkLoginIframe: false,
      scope: 'openid profile email administrator-scope',
    })
  }

  async getToken(minValidity = 30): Promise<string> {
    let refreshed = true
    try {
      await this.kc.updateToken(minValidity)
    } catch {
      // No refresh token available (check-sso without offline session), or Keycloak unreachable.
      // Fall through and use the current token if it is still valid.
      refreshed = false
    }
    // An expired token that couldn't be refreshed is as good as none: OSB would refuse it.
    if (!this.kc.token || (!refreshed && this.kc.isTokenExpired())) {
      this.sessionEnded()
      throw new Error('No access token available. Please sign in again.')
    }
    return this.kc.token
  }

  getLoginUrl(redirectUri: string): Promise<string> {
    return this.kc.createLoginUrl({ redirectUri })
  }

  async isReachable(timeoutMs = 5000): Promise<boolean> {
    try {
      // Any response counts (no-cors makes it opaque); only a network failure or the timeout doesn't.
      await fetch(this.realmUrl, { mode: 'no-cors', cache: 'no-store', signal: AbortSignal.timeout(timeoutMs) })
      return true
    } catch {
      return false
    }
  }

  login(): void {
    void this.kc.login()
  }

  logout(): void {
    void this.kc.logout({ redirectUri: window.location.origin + '/' })
  }

  get tokenParsed(): Record<string, unknown> | null {
    return (this.kc.tokenParsed as Record<string, unknown>) ?? null
  }
}
